import { CodeBlock, Alert, Table, Step, Card, CardGrid } from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function ProvablyFair() {
	return (
		<div className={styles.docsPage}>
			<h1>Provably Fair Gaming</h1>
			<p className={styles.lead}>
				Ringfort Studio implements provably fair algorithms for all games, allowing players to
				independently verify that every game outcome was fair and not manipulated.
			</p>

			<h2>What is Provably Fair?</h2>
			<p>
				Provably fair is a cryptographic method that allows players to verify the fairness of
				each game outcome. Unlike traditional online gaming where players must trust the operator,
				provably fair gaming provides mathematical proof that outcomes were determined fairly.
			</p>

			<h3>Key Principles</h3>
			<ul>
				<li><strong>Commitment:</strong> Before any bet, the server commits to an outcome by publishing a hash</li>
				<li><strong>Player Input:</strong> Players can contribute their own randomness via a client seed</li>
				<li><strong>Deterministic:</strong> The same inputs always produce the same output</li>
				<li><strong>Verifiable:</strong> After the game, players can verify the outcome independently</li>
				<li><strong>Transparent:</strong> The algorithm is public and can be audited</li>
			</ul>

			<Alert variant="info" title="Trust Through Mathematics">
				With provably fair gaming, trust is established through cryptographic verification rather
				than reputation. Players do not need to trust that Ringfort is honest - they can prove it.
			</Alert>

			<h2>How Ringfort Implements Provably Fair</h2>
			<p>
				Ringfort uses the industry-standard HMAC-SHA256 algorithm to generate verifiable random
				outcomes. Here is how the complete process works:
			</p>

			<Step number={1} title="Server Seed Generation">
				<p>
					When a player starts a new session or rotates their seeds, the server generates a
					cryptographically secure random 32-byte (256-bit) server seed.
				</p>
				<CodeBlock language="typescript" title="Server Seed Generation">
{`// Generate 32 random bytes, convert to hex string
const serverSeed = crypto.getRandomValues(new Uint8Array(32));
const serverSeedHex = Array.from(serverSeed)
  .map(b => b.toString(16).padStart(2, '0'))
  .join('');

// Example output:
// "a1b2c3d4e5f6789012345678abcdef0123456789abcdef0123456789abcdef01"`}
				</CodeBlock>
			</Step>

			<Step number={2} title="Server Seed Hash (Commitment)">
				<p>
					The server creates a SHA-256 hash of the server seed. This hash is shown to the player
					BEFORE any bets are placed. The hash acts as a cryptographic commitment - it proves
					the server seed was determined before the bet, but does not reveal the seed itself.
				</p>
				<CodeBlock language="typescript" title="Creating the Commitment Hash">
{`// Hash the server seed
const serverSeedHash = await crypto.subtle.digest(
  'SHA-256',
  new TextEncoder().encode(serverSeed)
);

// Convert to hex string
const hashHex = Array.from(new Uint8Array(serverSeedHash))
  .map(b => b.toString(16).padStart(2, '0'))
  .join('');

// Example output:
// "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"`}
				</CodeBlock>
				<Alert variant="success" title="Why This Matters">
					Since the hash is shown before the bet, the server cannot change the server seed
					after seeing the player's bet. Any change would produce a different hash.
				</Alert>
			</Step>

			<Step number={3} title="Client Seed">
				<p>
					The client seed is provided by the player or generated automatically. This ensures
					the player has input into the randomness, preventing the server from pre-computing
					favorable outcomes for the house.
				</p>
				<CodeBlock language="typescript" title="Client Seed Options">
{`// Option 1: Player provides their own seed
const clientSeed = "my_custom_seed_123";

// Option 2: Auto-generated (16 random bytes)
const autoClientSeed = crypto.getRandomValues(new Uint8Array(16));
const clientSeedHex = Array.from(autoClientSeed)
  .map(b => b.toString(16).padStart(2, '0'))
  .join('');

// Example: "7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c"`}
				</CodeBlock>
				<p>
					Players can change their client seed at any time through the game interface. This is
					recommended periodically to maximize randomness.
				</p>
			</Step>

			<Step number={4} title="Nonce (Bet Counter)">
				<p>
					The nonce is an incrementing counter that starts at 0 and increases by 1 for each bet.
					This ensures that even with the same server seed and client seed, each bet produces
					a unique outcome.
				</p>
				<CodeBlock language="typescript" title="Nonce Usage">
{`// Nonce increments with each bet
// Bet 1: nonce = 0
// Bet 2: nonce = 1
// Bet 3: nonce = 2
// ...

// Combined with seeds for unique outcomes:
const message = \`\${clientSeed}:\${nonce}\`;
// "7f8a9b0c1d2e3f4a:0" -> first bet
// "7f8a9b0c1d2e3f4a:1" -> second bet
// "7f8a9b0c1d2e3f4a:2" -> third bet`}
				</CodeBlock>
			</Step>

			<Step number={5} title="Outcome Generation">
				<p>
					The final outcome is generated using HMAC-SHA256, combining all the elements:
				</p>
				<CodeBlock language="typescript" title="HMAC-SHA256 Outcome Generation">
{`// The core provably fair formula:
// outcome = HMAC-SHA256(serverSeed, clientSeed + ":" + nonce)

async function generateOutcome(
  serverSeed: string,
  clientSeed: string,
  nonce: number
): Promise<string> {
  const message = \`\${clientSeed}:\${nonce}\`;

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(serverSeed),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(message)
  );

  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Example:
// serverSeed: "a1b2c3d4..."
// clientSeed: "7f8a9b0c..."
// nonce: 0
// output: "f4d5e6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5"`}
				</CodeBlock>
			</Step>

			<h2>Verification Algorithm by Game</h2>
			<p>
				Each game type converts the HMAC output into a game-specific result. Here is exactly
				how each game calculates outcomes:
			</p>

			<h3>Dice Game Verification</h3>
			<p>
				The Dice game generates a roll between 0.00 and 100.00 using 10,001 possible outcomes
				(0 to 10,000, then divided by 100).
			</p>

			<CodeBlock language="typescript" title="Dice Roll Calculation">
{`async function calculateDiceRoll(
  serverSeed: string,
  clientSeed: string,
  nonce: number
): Promise<number> {
  // Step 1: Generate HMAC-SHA256 hash
  const hash = await generateOutcome(serverSeed, clientSeed, nonce);

  // Step 2: Take first 8 hex characters (4 bytes = 32 bits)
  const hexSlice = hash.slice(0, 8);

  // Step 3: Convert to number (0 to 4,294,967,295)
  const decimalValue = parseInt(hexSlice, 16);

  // Step 4: Modulo 10001 to get 0-10000
  const rollInt = decimalValue % 10001;

  // Step 5: Divide by 100 for final roll (0.00 to 100.00)
  const roll = rollInt / 100;

  return Number(roll.toFixed(2));
}

// Example calculation:
// hash = "f4d5e6a7b8c9d0e1..."
// hexSlice = "f4d5e6a7"
// decimalValue = 4107175591
// rollInt = 4107175591 % 10001 = 2580
// roll = 2580 / 100 = 25.80`}
			</CodeBlock>

			<h4>Win Determination</h4>
			<CodeBlock language="typescript" title="Dice Win Logic">
{`function isDiceWin(
  roll: number,
  target: number,
  condition: 'over' | 'under'
): boolean {
  if (condition === 'under') {
    return roll < target;  // Win if roll is strictly less than target
  } else {
    return roll > target;  // Win if roll is strictly greater than target
  }
}

// Examples:
// Target: 50, Condition: under, Roll: 25.80 -> WIN (25.80 < 50)
// Target: 50, Condition: over, Roll: 25.80 -> LOSE (25.80 is not > 50)
// Target: 25, Condition: under, Roll: 25.80 -> LOSE (25.80 is not < 25)`}
			</CodeBlock>

			<h3>Plinko Game Verification</h3>
			<p>
				Plinko determines the ball's path through the pegs. At each row, the ball goes either
				left (0) or right (1), determined by a unique hash for each row.
			</p>

			<CodeBlock language="typescript" title="Plinko Path Calculation">
{`async function calculatePlinkoPath(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  rows: number
): Promise<number[]> {
  const path: number[] = [];

  for (let row = 0; row < rows; row++) {
    // Generate unique hash for each row
    // Note: We modify the client seed per row
    const rowHash = await generateOutcome(
      serverSeed,
      \`\${clientSeed}:\${row}\`,
      nonce
    );

    // Take first 2 hex chars (1 byte)
    const byte = parseInt(rowHash.slice(0, 2), 16);

    // Direction: even = left (0), odd = right (1)
    const direction = byte % 2;
    path.push(direction);
  }

  return path;
}

// Example for 8 rows:
// Row 0 hash: "a4..." -> byte 164 -> 164 % 2 = 0 (left)
// Row 1 hash: "7b..." -> byte 123 -> 123 % 2 = 1 (right)
// Row 2 hash: "f2..." -> byte 242 -> 242 % 2 = 0 (left)
// ... and so on
// Path: [0, 1, 0, 1, 1, 0, 0, 1]`}
			</CodeBlock>

			<h4>Bucket Determination</h4>
			<CodeBlock language="typescript" title="Plinko Bucket Calculation">
{`function pathToBucket(path: number[]): number {
  // Bucket = number of times ball went right
  return path.reduce((sum, direction) => sum + direction, 0);
}

// Example:
// Path: [0, 1, 0, 1, 1, 0, 0, 1]
// Bucket: 0+1+0+1+1+0+0+1 = 4

// For 8 rows, buckets range from 0 to 8
// Bucket 0 = always went left (rare, high multiplier on edges)
// Bucket 4 = balanced (common, lower multiplier in middle)
// Bucket 8 = always went right (rare, high multiplier on edges)`}
			</CodeBlock>

			<h3>Coin Flip Game Verification</h3>
			<p>
				Coin Flip is the simplest game - it determines heads or tails based on whether
				the hash produces an even or odd number.
			</p>

			<CodeBlock language="typescript" title="Coin Flip Calculation">
{`async function calculateCoinFlip(
  serverSeed: string,
  clientSeed: string,
  nonce: number
): Promise<'heads' | 'tails'> {
  // Generate HMAC-SHA256 hash
  const hash = await generateOutcome(serverSeed, clientSeed, nonce);

  // Take first 8 hex characters (4 bytes)
  const hexSlice = hash.slice(0, 8);
  const decimalValue = parseInt(hexSlice, 16);

  // Even = heads, Odd = tails
  return decimalValue % 2 === 0 ? 'heads' : 'tails';
}

// Example:
// hash = "f4d5e6a7..."
// hexSlice = "f4d5e6a7"
// decimalValue = 4107175591
// 4107175591 % 2 = 1 -> 'tails'`}
			</CodeBlock>

			<h3>Crash Game Verification</h3>
			<p>
				Crash calculates the crash point (multiplier) where the game ends. This uses a more
				complex formula to ensure proper distribution and house edge.
			</p>

			<CodeBlock language="typescript" title="Crash Point Calculation">
{`async function calculateCrashPoint(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  houseEdge: number = 0.01  // 1% house edge
): Promise<number> {
  // Generate HMAC-SHA256 hash
  const hash = await generateOutcome(serverSeed, clientSeed, nonce);

  // Use first 13 hex characters (52 bits of precision)
  const h = parseInt(hash.slice(0, 13), 16);
  const e = Math.pow(2, 52);  // Maximum value for this precision

  // Convert to random value between 0 and 1
  const r = h / e;

  // Edge case: prevent division by zero
  if (r === 0) {
    return 1.0;  // Instant crash
  }

  // Calculate crash point with house edge
  // Formula: crashPoint = (1 - houseEdge) / r
  // This ensures E[1/crashPoint] = 1 - houseEdge
  const crashPoint = (1 - houseEdge) / r;

  // Minimum crash point is 1.00x
  return Math.max(1.0, Number(crashPoint.toFixed(2)));
}

// Example calculation:
// hash = "f4d5e6a7b8c9d..."
// h = parseInt("f4d5e6a7b8c9d", 16) = 270853232348317
// e = 4503599627370496
// r = 270853232348317 / 4503599627370496 = 0.0601...
// crashPoint = 0.99 / 0.0601 = 16.47x`}
			</CodeBlock>

			<Alert variant="warning" title="Instant Crash Probability">
				Due to the mathematical distribution, there is a small chance (approximately equal to
				the house edge) of an instant crash at 1.00x. This is expected behavior and part of
				what creates the house edge.
			</Alert>

			<h2>Complete Verification Code</h2>
			<p>
				Here is a complete, standalone verification script that players can use to verify
				any bet outcome:
			</p>

			<CodeBlock language="typescript" title="Complete Verification Script" showLineNumbers>
{`/**
 * Ringfort Studio - Provably Fair Verification Script
 * Run this code to verify any game outcome
 */

// HMAC-SHA256 implementation using Web Crypto API
async function hmacSha256(key: string, message: string): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(message);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);

  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// SHA-256 hash
async function sha256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);

  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Verify server seed matches hash
async function verifyServerSeed(
  serverSeed: string,
  serverSeedHash: string
): Promise<boolean> {
  const computedHash = await sha256(serverSeed);
  return computedHash === serverSeedHash;
}

// Dice verification
async function verifyDice(
  serverSeed: string,
  clientSeed: string,
  nonce: number
): Promise<{ roll: number }> {
  const hash = await hmacSha256(serverSeed, \`\${clientSeed}:\${nonce}\`);
  const num = parseInt(hash.slice(0, 8), 16);
  const roll = (num % 10001) / 100;
  return { roll: Number(roll.toFixed(2)) };
}

// Plinko verification
async function verifyPlinko(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  rows: number
): Promise<{ path: number[]; bucket: number }> {
  const path: number[] = [];

  for (let i = 0; i < rows; i++) {
    const hash = await hmacSha256(serverSeed, \`\${clientSeed}:\${i}:\${nonce}\`);
    const byte = parseInt(hash.slice(0, 2), 16);
    path.push(byte % 2);
  }

  const bucket = path.reduce((sum, dir) => sum + dir, 0);
  return { path, bucket };
}

// Coin Flip verification
async function verifyCoinFlip(
  serverSeed: string,
  clientSeed: string,
  nonce: number
): Promise<{ result: 'heads' | 'tails' }> {
  const hash = await hmacSha256(serverSeed, \`\${clientSeed}:\${nonce}\`);
  const num = parseInt(hash.slice(0, 8), 16);
  return { result: num % 2 === 0 ? 'heads' : 'tails' };
}

// Crash verification
async function verifyCrash(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  houseEdge: number = 0.01
): Promise<{ crashPoint: number }> {
  const hash = await hmacSha256(serverSeed, \`\${clientSeed}:\${nonce}\`);
  const h = parseInt(hash.slice(0, 13), 16);
  const e = Math.pow(2, 52);
  const r = h / e;

  if (r === 0) {
    return { crashPoint: 1.0 };
  }

  const crashPoint = (1 - houseEdge) / r;
  return { crashPoint: Math.max(1.0, Number(crashPoint.toFixed(2))) };
}

// Main verification function
async function verify(params: {
  game: 'dice' | 'plinko' | 'coinflip' | 'crash';
  serverSeed: string;
  serverSeedHash: string;
  clientSeed: string;
  nonce: number;
  rows?: number;  // For Plinko
}) {
  // First, verify the server seed
  const seedValid = await verifyServerSeed(
    params.serverSeed,
    params.serverSeedHash
  );

  if (!seedValid) {
    console.error('VERIFICATION FAILED: Server seed does not match hash!');
    return null;
  }

  console.log('Server seed verified successfully.');

  // Verify game outcome
  switch (params.game) {
    case 'dice':
      return verifyDice(params.serverSeed, params.clientSeed, params.nonce);
    case 'plinko':
      return verifyPlinko(
        params.serverSeed,
        params.clientSeed,
        params.nonce,
        params.rows || 8
      );
    case 'coinflip':
      return verifyCoinFlip(params.serverSeed, params.clientSeed, params.nonce);
    case 'crash':
      return verifyCrash(params.serverSeed, params.clientSeed, params.nonce);
  }
}

// Example usage:
verify({
  game: 'dice',
  serverSeed: 'a1b2c3d4e5f6789012345678abcdef0123456789abcdef0123456789abcdef01',
  serverSeedHash: 'e3b0c44298fc1c149afbf4c8996fb924...',
  clientSeed: '7f8a9b0c1d2e3f4a',
  nonce: 0
}).then(result => console.log('Result:', result));`}
			</CodeBlock>

			<h2>Third-Party Verification Tools</h2>
			<p>
				Players can also use independent verification tools to confirm outcomes:
			</p>

			<CardGrid columns={2}>
				<Card
					title="Online Verifier"
					description="Use our web-based verification tool to check any bet without writing code."
				>
					<p style={{ marginTop: '1rem', fontSize: 'var(--text-sm)' }}>
						<a href="https://verify.ringfort.studio" target="_blank" rel="noopener noreferrer">
							verify.ringfort.studio
						</a>
					</p>
				</Card>
				<Card
					title="Open Source Library"
					description="Use our published npm package to verify outcomes in your own code."
				>
					<CodeBlock language="bash">
{`npm install @ringfort/provably-fair`}
					</CodeBlock>
				</Card>
			</CardGrid>

			<h3>Using the NPM Package</h3>
			<CodeBlock language="typescript" title="NPM Package Usage">
{`import {
  verifyDiceBet,
  verifyPlinkoBet,
  verifyCoinFlipBet,
  verifyCrashRound
} from '@ringfort/provably-fair';

// Verify a dice bet
const diceResult = await verifyDiceBet(
  {
    serverSeed: 'revealed_server_seed_here',
    serverSeedHash: 'hash_shown_before_bet',
    clientSeed: 'your_client_seed',
    nonce: 42
  },
  target: 50,
  condition: 'under'
);

console.log(diceResult);
// {
//   valid: true,
//   serverSeedValid: true,
//   outcome: { roll: 25.80, target: 50, condition: 'under', won: true }
// }`}
			</CodeBlock>

			<h2>Transparency and Audit Trail</h2>
			<p>
				Ringfort maintains a complete audit trail for all provably fair operations:
			</p>

			<h3>What We Store</h3>
			<Table
				columns={[
					{ key: 'data', header: 'Data' },
					{ key: 'when', header: 'When Stored' },
					{ key: 'retention', header: 'Retention' },
				]}
				data={[
					{ data: 'Server Seed Hash', when: 'Before any bets', retention: 'Permanent' },
					{ data: 'Server Seed (encrypted)', when: 'At generation', retention: 'Until revealed' },
					{ data: 'Client Seed', when: 'At each bet', retention: 'Permanent' },
					{ data: 'Nonce', when: 'At each bet', retention: 'Permanent' },
					{ data: 'Revealed Server Seed', when: 'After seed rotation', retention: 'Permanent' },
					{ data: 'Game Outcome', when: 'After each bet', retention: 'Permanent' },
				]}
			/>

			<h3>Accessing Verification Data</h3>
			<p>
				Players can access all verification data through the game interface:
			</p>
			<ul>
				<li>Click the "Fairness" or shield icon in any game</li>
				<li>View current server seed hash and client seed</li>
				<li>See bet history with full verification data</li>
				<li>Access one-click verification for any past bet</li>
			</ul>

			<h3>API Access</h3>
			<CodeBlock language="bash" title="Get Verification Data via API">
{`# Get verification data for a specific bet
curl https://api.ringfort.studio/v1/bets/{bet_id}/verification \\
  -H "Authorization: Bearer your_api_key"

# Response includes full provably fair data:
{
  "bet_id": "bet_abc123",
  "game": "dice",
  "server_seed": "revealed_if_seed_rotated",
  "server_seed_hash": "e3b0c44298fc1c149afbf4c8996fb924...",
  "client_seed": "7f8a9b0c1d2e3f4a",
  "nonce": 42,
  "outcome": {
    "roll": 25.80,
    "target": 50,
    "condition": "under",
    "won": true
  },
  "verification_url": "https://verify.ringfort.studio/bet/bet_abc123"
}`}
			</CodeBlock>

			<Alert variant="success" title="Independent Audits">
				Ringfort's provably fair implementation has been audited by third-party security firms.
				Audit reports are available upon request for licensed operators.
			</Alert>

			<h2>Frequently Asked Questions</h2>

			<h3>Can the server change the outcome after I bet?</h3>
			<p>
				No. The server seed hash is published before you bet. Any change to the server seed
				would produce a different hash, which you can verify after the seed is revealed.
			</p>

			<h3>Why should I change my client seed?</h3>
			<p>
				Changing your client seed periodically ensures maximum randomness. While the server
				cannot predict outcomes (since they depend on your client seed), rotating seeds is
				a good security practice.
			</p>

			<h3>When is the server seed revealed?</h3>
			<p>
				The server seed is revealed when you rotate to a new seed pair, or when your session
				ends. This allows you to verify all bets made with that seed.
			</p>

			<h3>What if my verification does not match?</h3>
			<p>
				If your independent verification does not match the game outcome, please contact
				support immediately. This should never happen if the system is working correctly.
				Include your bet ID, all seed information, and your verification results.
			</p>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<CardGrid columns={2}>
					<Card
						title="Testing Guide"
						description="Learn how to test your integration thoroughly before going live."
						href="/docs/testing-guide"
					/>
					<Card
						title="Game Documentation"
						description="Detailed documentation for each game type and their mechanics."
						href="/docs/games-dice"
					/>
				</CardGrid>
			</div>
		</div>
	);
}
